import clsx from 'clsx';
import type { FunctionComponent, HTMLAttributes, ReactNode } from 'react';
import { createContext, forwardRef, useContext, useMemo } from 'react';

import { Button } from '../button/Button';
import { RecessedSurface } from '../recessed-surface/RecessedSurface';

export type CardSurface = 'default' | 'frosted' | 'recessed';

export type CardProps = HTMLAttributes<HTMLDivElement> & {
    readonly children: ReactNode;
    readonly disabled?: boolean;
    readonly isActionPending?: boolean;
    readonly isActive?: boolean;
    readonly isCopied?: boolean;
    readonly isCut?: boolean;
    readonly isRaised?: boolean;
    readonly isSelected?: boolean;
    readonly surface?: CardSurface;
};

type CardContextValue = {
    readonly isActive: boolean;
    readonly isSelected: boolean;
    readonly surface: CardSurface;
};

const CardContext = createContext<CardContextValue>({ isActive: false, isSelected: false, surface: 'default' });

export const useCardContext = (): CardContextValue => useContext(CardContext);

export const CardRow: FunctionComponent<HTMLAttributes<HTMLDivElement> & { readonly children: ReactNode }> = ({
    children,
    className,
    ...props
}) => (
    <div
        className={clsx('flex flex-row flex-1 shrink items-center justify-center gap-s min-w-0 p-0 w-full', className)}
        {...props}
    >
        {children}
    </div>
);

export const CardText: FunctionComponent<HTMLAttributes<HTMLSpanElement> & { readonly children: ReactNode }> = ({
    children,
    className,
    ...props
}) => (
    <span
        className={clsx(
            'flex flex-1 gap-xs justify-center min-w-0 w-full',
            'text-ui font-medium text-text-content-strong tracking-[-0.14px]',
            'overflow-hidden text-ellipsis whitespace-nowrap select-none',
            className,
        )}
        {...props}
    >
        {children}
    </span>
);

export const CardButton: FunctionComponent<{
    readonly children: ReactNode;
    readonly className?: string;
    readonly isActive: boolean;
    readonly isSelected: boolean;
    readonly [key: string]: unknown;
}> = ({ children, className, isActive, isSelected, ...props }) => (
    <Button
        className={clsx(
            'text-text-content-subtle',
            'hover:opacity-80',
            'focus-visible:opacity-80 focus-visible:outline focus-visible:outline-1 focus-visible:outline-focus-ring',
            isActive && 'text-text-on-accent hover:bg-transparent hover:text-text-on-accent',
            isSelected &&
                '[&>button]:bg-transparent [&>button]:text-text-content-subtle [&>button]:hover:bg-transparent',
            className,
        )}
        {...props}
    >
        {children}
    </Button>
);

export const SmallRow: FunctionComponent<HTMLAttributes<HTMLDivElement> & { readonly children: ReactNode }> = ({
    children,
    className,
    ...props
}) => (
    <div className={clsx('flex items-center text-text-content-faint text-meta gap-xs pt-xs', className)} {...props}>
        {children}
    </div>
);

// Shared frosted-glass surface recipe, also used standalone (e.g. dashboard panels).
export const FROSTED_SURFACE_CLASSES =
    'rounded-xxl bg-surface-frosted backdrop-blur-[14px] backdrop-saturate-[1.04] outline outline-1 -outline-offset-1 outline-surface-edge';

const surfaceBaseClassName: Record<CardSurface, string> = {
    default: 'rounded-xl px-xl py-xl shadow-sm outline outline-1 -outline-offset-1',
    frosted: clsx(FROSTED_SURFACE_CLASSES, 'p-xxl shadow-sm'),
    recessed: 'w-full rounded-recessed-pocket',
};

const getCardClassName = ({
    className,
    disabled,
    isActionPending,
    isActive,
    isCopied,
    isCut,
    isRaised,
    isSelected,
    surface = 'default',
}: Pick<
    CardProps,
    | 'className'
    | 'disabled'
    | 'isActionPending'
    | 'isActive'
    | 'isCopied'
    | 'isCut'
    | 'isRaised'
    | 'isSelected'
    | 'surface'
>): string => {
    const isDefaultSurface = surface === 'default';
    const isSelectedDefaultSurface = isSelected === true && isActive !== true && isDefaultSurface;

    return clsx(
        'group/card flex flex-col cursor-pointer overflow-hidden font-sans',
        'transition-[background-color,box-shadow] duration-200 ease-in-out will-change-[background-color,box-shadow]',
        'focus-visible:outline focus-visible:outline-1 focus-visible:outline-focus-ring',
        surfaceBaseClassName[surface],
        isDefaultSurface && (isSelectedDefaultSurface ? 'outline-card-selection-edge' : 'outline-surface-edge'),
        disabled === true && 'opacity-50',
        isRaised === true && 'shadow-surface-raised',
        isSelectedDefaultSurface && 'bg-surface-raised text-text-content-subtle',
        isSelected === true && surface === 'recessed' && 'outline-none!',
        isActive === true && isDefaultSurface && 'bg-accent text-text-on-accent hover:shadow-sm',
        isCopied === true && isActive === true && 'border border-dashed border-border-control',
        isCopied === true &&
            isActive !== true &&
            (isDefaultSurface ? 'bg-surface-subtle text-text-content-strong' : 'text-text-content-strong'),
        isCut === true && 'border border-dotted border-outline-selected opacity-80',
        isActionPending === true && 'pending-copy-border pointer-events-none opacity-70',
        isDefaultSurface &&
            isActive !== true &&
            isCopied !== true &&
            isActionPending !== true &&
            'bg-surface-raised hover:bg-surface-hover',
        className,
    );
};

//TODO:
// Remove the isCut, isCopied props as soon as UX is clear on how it should work
export const Card = forwardRef<HTMLDivElement, CardProps>(
    (
        {
            children,
            className,
            disabled,
            isActionPending,
            isActive,
            isCopied,
            isCut,
            isRaised,
            isSelected,
            surface = 'default',
            ...props
        },
        ref,
    ) => {
        const contextValue = useMemo(
            () => ({ isActive: isActive === true, isSelected: isSelected === true, surface }),
            [isActive, isSelected, surface],
        );

        const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
            if (event.target === event.currentTarget && props.onClick !== undefined) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    props.onClick(event as unknown as React.MouseEvent<HTMLDivElement>);
                }
            }
        };

        const content =
            surface === 'recessed' ? (
                <RecessedSurface
                    active={isSelected === true}
                    hoverActive={isSelected !== true && isActionPending !== true}
                    hoverPocketTone="neutral"
                    hoverSurfaceShadow="sm"
                    hoverSurfaceTone="gray"
                    inactiveSurfaceShadow="sm"
                    inactiveSurfaceTone="white"
                    pocketTone={isActive === true ? 'neutral' : 'orange'}
                    renderMode="overlay"
                    surfaceClassName="px-xl py-xl"
                    surfaceLayerClassName={clsx(
                        'outline outline-1 -outline-offset-1',
                        isActive === true ? 'outline-recessed-active-edge' : 'outline-surface-edge',
                    )}
                    surfaceShadow={isActive === true ? 'orange-sm' : 'sm'}
                    surfaceTone={isActive === true ? 'orange' : 'gray'}
                    transition="none"
                >
                    {children}
                </RecessedSurface>
            ) : (
                children
            );

        return (
            <CardContext.Provider value={contextValue}>
                {/* biome-ignore lint/a11y/noStaticElementInteractions: suppressed due to migration */}
                <div
                    ref={ref}
                    className={getCardClassName({
                        className,
                        disabled,
                        isActionPending,
                        isActive,
                        isCopied,
                        isCut,
                        isRaised,
                        isSelected,
                        surface,
                    })}
                    data-is-active={isActive}
                    data-is-copied={isCopied}
                    data-is-cut={isCut}
                    data-is-selected={isSelected}
                    data-testid="card-component"
                    role={props.onClick !== undefined ? 'button' : undefined}
                    tabIndex={isActionPending === true ? undefined : props.onClick !== undefined ? 0 : undefined}
                    onKeyDown={props.onClick !== undefined ? handleKeyDown : undefined}
                    {...props}
                >
                    {content}
                </div>
            </CardContext.Provider>
        );
    },
);
